import { useState, useCallback, useEffect } from "react";
import axios from "../customAxios";
import { capitalizeWords } from "../utils/functions";
import { getImagesUrls } from "../utils/s3Utils";
import { useWarehouses } from "./useWarehouses";
import { useAuth } from "./useAuth";

const imagesKeyRoutes = {
  s3Woocommerce: "assets/images/woocommerce.png",
  s3Shopify: "assets/images/shopify.png",
  s3MercadoLibre: "assets/images/mercadolibre.png",
  s3Bootic: "assets/images/bootic.png",
  s3Prestashop: "assets/images/prestashop.png",
  s3Vtex: "assets/images/vtex.png",
};
const imageUrls = await getImagesUrls(imagesKeyRoutes, "ecommerces");

export const useEcommerceIntegrations = () => {
  // Data status and IU
  const { user } = useAuth();
  const { warehouses, fetchSettingEcommerceWarehouses } = useWarehouses();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableEcommerces, setAvailableEcommerces] = useState([
    {
      name: "Shopify",
      type: "webhook",
      warehouse: "",
      image: imageUrls.s3Shopify,
      tutorial_link:
        "https://public-nh.s3.us-east-1.amazonaws.com/tutorials/ninja-shopify-webhooks.pdf",
      configuration: {
        options: {
          enabled_delivery_methods: [],
        },
        authentication: {
          shop_url: "",
          api_key: "",
          client_secret: "",
          token: "",
        },
        create_order: {
          url: `${process.env.REACT_APP_URL}shopify/create_order/${user.seller_id}/`,
          enabled: true,
        },
        cancel_order: {
          url: `${process.env.REACT_APP_URL}shopify/cancel_order/${user.seller_id}/`,
          enabled: true,
        },
        update_order: {
          url: `${process.env.REACT_APP_URL}shopify/update_order/${user.seller_id}/`,
          enabled: true,
        },
      },
    },
    {
      name: "Woocommerce",
      type: "webhook",
      warehouse: "",
      image: imageUrls.s3Woocommerce,
      tutorial_link:
        "https://public-nh.s3.us-east-1.amazonaws.com/tutorials/ninja-woocommerce-webhooks.pdf",
      configuration: {
        options: {
          enabled_delivery_methods: [],
        },
        authentication: {
          token: "",
          woocommerce_user: "",
          woocommerce_password: "",
        },
        create_order: {
          url: `${process.env.REACT_APP_URL}woocommerce/standard/create_order/${user.seller_id}/`,
          enabled: true,
        },
        create_order_plugin: {
          url: `${process.env.REACT_APP_URL}woocommerce/ninja_plugin/create_order/${user.seller_id}/`,
          enabled: true,
        },
        cancel_order: {
          url: `${process.env.REACT_APP_URL}woocommerce/cancel_order/${user.seller_id}/`,
          enabled: true,
        },
        update_order: {
          url: `${process.env.REACT_APP_URL}woocommerce/update_order/${user.seller_id}/`,
          enabled: true,
        },
      },
    },
    {
      name: "Prestashop",
      type: "webhook",
      warehouse: "",
      image: imageUrls.s3Prestashop,
      tutorial_link:
        "https://public-nh.s3.us-east-1.amazonaws.com/tutorials/ninja-prestashop-webhooks.pdf",
      configuration: {
        options: {
          enabled_delivery_methods: [],
        },
        authentication: {
          shop_name: "",
          shop_url: "",
          token: "",
        },
        create_order: {
          url: `${process.env.REACT_APP_URL}prestashop/create_order/${user.seller_id}/`,
          enabled: true,
        },
      },
    },
    {
      name: "Mercado Libre",
      type: "webhook",
      warehouse: "",
      image: imageUrls.s3MercadoLibre,
      tutorial_link:
        "https://public-nh.s3.us-east-1.amazonaws.com/tutorials/ninja-mercadolibre-webhooks.pdf",
      configuration: {
        authentication: {
          integration_user_id: "",
          token: "",
          refresh_token: "",
        },
        options: {
          fulfillment: false,
          xd_drop_off: false,
          cross_docking: false,
          self_service: true,
          turbo: true,
          not_specify: true,
          custom: true,
          automatic_print_label: true,
        },
        create_order: {
          url: `${process.env.REACT_APP_URL}meli/notifications/`,
          enabled: true,
        },
      },
    },
    {
      name: "Bootic",
      type: "webhook",
      warehouse: "",
      image: imageUrls.s3Bootic,
      tutorial_link:
        "https://public-nh.s3.us-east-1.amazonaws.com/tutorials/ninja-bootic-webhooks.pdf",
      configuration: {
        authentication: {
          token: "",
          refresh_token: "",
        },
        options: {
          enabled_delivery_methods: [],
        },
        create_order: {
          url: `${process.env.REACT_APP_URL}bootic/create_order/${user.seller_id}/`,
          enabled: true,
        },
        cancel_order: {
          url: `${process.env.REACT_APP_URL}bootic/cancel_order/${user.seller_id}/`,
          enabled: true,
        },
        update_order: {
          url: `${process.env.REACT_APP_URL}bootic/update_order/${user.seller_id}/`,
          enabled: true,
        },
      },
    },
    {
      name: "VTex",
      type: "webhook",
      warehouse: "",
      image: imageUrls.s3Vtex,
      tutorial_link:
        "https://public-nh.s3.us-east-1.amazonaws.com/tutorials/ninja-vtex-webhooks.pdf",
      configuration: {
        authentication: {
          shop_name: "",
          app_key: "",
          app_token: "",
        },
        options: {
          enabled_delivery_methods: [],
        },
        create_order: {
          url: `${process.env.REACT_APP_URL}vtex/create_order/${user.seller_id}`,
          enabled: true,
        },
      },
    },
  ]);

  // STATE MANNERS (HANDLERS)
  const fetchIntegrations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const imageUrls = await getImagesUrls(imagesKeyRoutes, "ecommerces");
      const { data } = await axios.get("companies/setting_ecommerces/");
      const processedData = data.map((ecommerce) => {
        const imageKey = `s3${capitalizeWords(ecommerce.name).split(" ").join("")}`;
        return {
          ...ecommerce,
          image: imageUrls[imageKey] || null,
        };
      });

      setIntegrations(processedData);
    } catch (error) {
      console.error(`Error fetching integrations: ${error}`);
      setError("No se pudieron cargar las integraciones.");
    } finally {
      setLoading(false);
    }
  }, []);

  const createIntegration = useCallback(
    async (integrationData) => {
      setLoading(true);
      setError(null);
      try {
        await axios.post("companies/setting_ecommerces/create", {
          setting_ecommerce: integrationData,
        });
        await fetchIntegrations();
        return { success: true };
      } catch (error) {
        const msg =
          error.response?.data?.error || "Error al crear la integración.";
        setError(msg);
        return { success: false, error: msg };
      } finally {
        setLoading(false);
      }
    },
    [fetchIntegrations],
  );

  const updateIntegration = useCallback(
    async (id, integrationData) => {
      setLoading(true);
      setError(null);
      try {
        await axios.patch(`companies/setting_ecommerces/update/${id}`, {
          setting_ecommerce: integrationData,
        });
        await fetchIntegrations();
        return { success: true };
      } catch (error) {
        const msg =
          error.response?.data?.error || "Error al actualizar la integración.";
        setError(msg);
        return { success: false, error: msg };
      } finally {
        setLoading(false);
      }
    },
    [fetchIntegrations],
  );

  const deleteIntegration = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await axios.delete(`companies/setting_ecommerces/delete/${id}`);
      setIntegrations((prev) => prev.filter((item) => item.id !== id));
      return { success: true };
    } catch (error) {
      const msg =
        error.response?.data?.error || "Error al eliminar la integración.";
      setError(msg);
      return { success: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  const initMeliAuth = useCallback(async (integrationData) => {
    setLoading(true);
    try {
      const { data, status } = await axios.post(
        "companies/setting_ecommerces/meli/authorization/init/",
        { setting_ecommerce: integrationData },
      );

      if (status === 200 && data.url) {
        return { success: true, redirectUri: data.url, error: null };
      } else {
        throw new Error("Respuesta inválida del servidor");
      }
    } catch (error) {
      const msg = "Error al iniciar autorización con Mercado Libre";
      console.error(`Error al iniciar autorización con Mercado Libre: ${error}`);
      setError(msg);
      return { success: false, error: msg, redirectUri: null };
    } finally {
      setLoading(false);
    }
  }, []);

  const initShopifyAuth = useCallback(async (shopUrl, credentials = {}, warehouse) => {
    setLoading(true);
    try {
      const cleanShop = shopUrl
        .replace("https://", "")
        .replace("http://", "")
        .replace(/\/$/, "");

      const payload = {
        shop: cleanShop,
        client_id: credentials.clientId,
        client_secret: credentials.clientSecret,
        warehouse
      };

      const { data } = await axios.post(
        `companies/setting_ecommerces/shopify/authorization/init/`,
        payload,
      );

      if (data.url) {
        return { success: true, redirectUri: data.url, error: null };
      } else {
        return {
          success: false,
          redirectUri: null,
          error: "El servidor no devolvió la URL de autorización.",
        };
      }
    } catch (error) {
      console.error(`Shopify Auth Error: ${error}`);
      return {
        success: false,
        error:
          error.response?.data?.error ||
          "Error al conectar con Shopify. Verifica la URL y credenciales.",
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // SECONDARY EFFECTS (FETCH, DEBUNCE)
  useEffect(() => {
    fetchSettingEcommerceWarehouses();
  }, [fetchSettingEcommerceWarehouses]);

  // PUBLIC API OF THE HOOK
  return {
    // State and Data
    integrations,
    warehouses,
    loading,
    error,
    availableEcommerces,

    // MANNERS (Setters y Actions)
    setError,
    setLoading,
    setAvailableEcommerces,
    fetchIntegrations,
    createIntegration,
    updateIntegration,
    deleteIntegration,
    initMeliAuth,
    initShopifyAuth,
  };
};
